import type { AdminMenuRouteSource } from '@/shared/types/admin'

export interface AdminRouteMenuTarget {
  kind: 'route'
  routePath: string
}

export interface AdminNewPageMenuTarget {
  kind: 'new-page'
  href: string
  index: string
}

export interface AdminInvalidMenuTarget {
  kind: 'invalid'
  index: string
}

export type AdminMenuTarget =
  | AdminRouteMenuTarget
  | AdminNewPageMenuTarget
  | AdminInvalidMenuTarget

const safeMenuId = (id: string | number): string => {
  return String(id).replace(/[^a-zA-Z0-9_-]/g, '-')
}

export const normalizeAdminMenuPath = (menuUrl: string | null | undefined): string | null => {
  if (!menuUrl) return null

  const normalizedMenuUrl = menuUrl.trim()
  if (/SITE_CONFIG\[["']apiURL["']\].*\/doc\.html/i.test(normalizedMenuUrl)) {
    return '/admin/system/api-docs'
  }
  if (/SITE_CONFIG\[["']apiURL["']\].*\/druid\/sql\.html/i.test(normalizedMenuUrl)) {
    return '/admin/system/sql-monitor'
  }

  const withoutQuery = normalizedMenuUrl.split(/[?#]/, 1)[0]?.replaceAll('\\', '/') || ''
  if (!withoutQuery || /^[a-z][a-z\d+.-]*:/i.test(withoutQuery)) return null

  const segments = withoutQuery.split('/').filter(Boolean)
  if (segments.some(segment => segment === '.' || segment === '..')) return null
  if (segments[0] === 'admin') segments.shift()
  return segments.length ? `/admin/${segments.join('/')}` : null
}

/** 只允许无用户凭据的显式 HTTP(S) URL。 */
export const normalizeAdminExternalUrl = (
  menuUrl: string | null | undefined
): string | null => {
  if (!menuUrl) return null
  try {
    const parsed = new URL(menuUrl.trim())
    if (!['http:', 'https:'].includes(parsed.protocol)) return null
    if (parsed.username || parsed.password) return null
    return parsed.href
  } catch {
    return null
  }
}

export const toAdminExternalFramePath = (id: string | number): string => {
  return `/admin/external/${safeMenuId(id)}`
}

export const resolveAdminMenuTarget = (item: AdminMenuRouteSource): AdminMenuTarget => {
  const internalPath = normalizeAdminMenuPath(item.url)
  if (internalPath) {
    return item.openStyle === 1
      ? { kind: 'new-page', href: internalPath, index: `admin-new-page-${safeMenuId(item.id)}` }
      : { kind: 'route', routePath: internalPath }
  }

  const externalUrl = normalizeAdminExternalUrl(item.url)
  if (externalUrl) {
    return item.openStyle === 1
      ? { kind: 'new-page', href: externalUrl, index: `admin-new-page-${safeMenuId(item.id)}` }
      : { kind: 'route', routePath: toAdminExternalFramePath(item.id) }
  }

  return { kind: 'invalid', index: `admin-invalid-menu-${safeMenuId(item.id)}` }
}
